/* eslint-disable react-refresh/only-export-components */

import { createContext, use, useCallback, useMemo } from 'react';
import invariant from 'tiny-invariant';

import { CadRendererViewSyncProvider } from './bridges/view-hydration-bridge.js';
import { useCadRendererSyncSession } from './sync-session.js';

import type { ModelGrid, ModelType } from '@cadstrom/sigma-engine-shared';
import type { Context } from 'react';
import type { SerializableGraphicRenderState } from './bridges/layer-bridge.js';
import type { CadRendererSyncStatus } from './broadcast-manager.js';
import type {
	CadRendererDrawingManagerSyncValue,
	CadRendererGridSyncStoreValue,
	CadRendererGridSyncValue,
	CadRendererLayerSyncStoreValue,
	CadRendererLayerSyncValue,
	CadRendererSyncProviderProps,
} from './sync-types.js';

/*
 * CAD renderer sync is scoped to one project revision and shared across windows with BroadcastChannel.
 * `CadRendererSyncProvider` owns the sync session and exposes narrow feature hooks so callers only
 * depend on the sync slice they actually use.
 */

const CadRendererSyncStatusContext = createContext<CadRendererSyncStatus | null>(null);
const CadRendererDrawingManagerSyncContext = createContext<CadRendererDrawingManagerSyncValue | null>(null);
const CadRendererLayerSyncContext = createContext<CadRendererLayerSyncStoreValue | null>(null);
const CadRendererGridSyncContext = createContext<CadRendererGridSyncStoreValue | null>(null);

export function CadRendererSyncProvider({ children, ...options }: CadRendererSyncProviderProps) {
	const session = useCadRendererSyncSession(options);

	return (
		<CadRendererSyncStatusContext value={session.status}>
			<CadRendererDrawingManagerSyncContext value={session.drawingManager}>
				<CadRendererLayerSyncContext value={session.layers}>
					<CadRendererGridSyncContext value={session.grid}>
						<CadRendererViewSyncProvider value={session.view}>{children}</CadRendererViewSyncProvider>
					</CadRendererGridSyncContext>
				</CadRendererLayerSyncContext>
			</CadRendererDrawingManagerSyncContext>
		</CadRendererSyncStatusContext>
	);
}

/**
 * Reads the current cross-window sync status from context.
 */
export function useCadRendererSyncStatus(): CadRendererSyncStatus {
	return useRequiredContext(CadRendererSyncStatusContext, 'CadRendererSyncStatusContext not provided');
}

/**
 * Reads the drawing-manager registration API from the sync session.
 */
export function useCadRendererDrawingManagerSync(): CadRendererDrawingManagerSyncValue {
	return useRequiredContext(CadRendererDrawingManagerSyncContext, 'CadRendererDrawingManagerSyncContext not provided');
}

/**
 * Reads layer sync state and actions scoped to one layer set.
 */
export function useCadRendererLayerSync(layerSet: string): CadRendererLayerSyncValue {
	const store = useRequiredContext(CadRendererLayerSyncContext, 'CadRendererLayerSyncContext not provided');
	const setLayerVisibility = useCallback(
		(visibleLayerIds: readonly number[]) => store.setLayerVisibility(layerSet, visibleLayerIds),
		[layerSet, store],
	);
	const setLayerColorOverride = useCallback(
		(layerId: number, color: SerializableGraphicRenderState) => store.setLayerColorOverride(layerSet, layerId, color),
		[layerSet, store],
	);

	return useMemo(
		() => ({
			layerState: store.layerStates[layerSet],
			setLayerVisibility,
			setLayerColorOverride,
		}),
		[layerSet, setLayerColorOverride, setLayerVisibility, store.layerStates],
	);
}

/**
 * Reads grid settings and actions scoped to one design, its model type, and its model grid defaults.
 */
export function useCadRendererGridSync(
	designId: string,
	modelType: ModelType,
	modelGrid: ModelGrid,
): CadRendererGridSyncValue {
	const store = useRequiredContext(CadRendererGridSyncContext, 'CadRendererGridSyncContext not provided');
	const gridSettings = store.getGridSettings(designId, modelType, modelGrid);
	const setGridSettings = useCallback(
		(settings: Parameters<CadRendererGridSyncStoreValue['setGridSettings']>[3]) =>
			store.setGridSettings(designId, modelType, modelGrid, settings),
		[designId, modelGrid, modelType, store],
	);

	const defaultGridSettings = useMemo(
		() => ({ ...store.defaultGridAppearance, distance: modelGrid.distance, units: modelGrid.units }),
		[modelGrid.distance, modelGrid.units, store.defaultGridAppearance],
	);

	return useMemo(
		() => ({
			gridSettings,
			defaultGridSettings,
			setGridSettings,
		}),
		[defaultGridSettings, gridSettings, setGridSettings],
	);
}

/**
 * Reads a required React context and throws a targeted setup error when its provider is missing.
 */
function useRequiredContext<TValue>(context: Context<TValue | null>, message: string): TValue {
	const value = use(context);
	invariant(value !== null, message);
	return value;
}
